import {inject, Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {distinctUntilChanged, map} from 'rxjs/operators';
import {ChatAdvisorPageService, IPageContextProvider} from '@dcp/configurator';
import {CurrentProductService} from '@spartacus/storefront';
import {DcpActiveCartService} from '@app/services/dcp-active-cart.service';
import {DcpGlobalMessageService} from '@app/core/services/dcp-global-message.service';

/**
 * dcp-commerce-ui implementation of the @dcp/configurator IPageContextProvider
 * contract. Feeds page/error/business-model/product/cart context to the
 * ChatAdvisorContextService so the store-support-agent (DCP-57696) receives
 * per-page metadata via the sap-advisor-chat web component.
 *
 * Sources:
 * - page$          — ChatAdvisorPageService.currentPage$ (URL-driven page detector)
 * - errorCode$     — DcpGlobalMessageService active-messages tail; naturally resets
 *                    on NavigationEnd (see dcp-global-message.service.ts:61-69)
 * - businessModel$ — always null in this pass (product decision pending on
 *                    direct/indirect derivation rule); backend knowledge tool
 *                    already substitutes 'direct' when the field is absent
 * - productId$     — Spartacus CurrentProductService.getProduct(); null off PDP
 * - cartId$        — DcpActiveCartService.getActiveCartId(); empty string
 *                    coerced to null so pre-cart flows (registration, search)
 *                    emit a clean payload
 */
@Injectable({providedIn: 'root'})
export class DcpPageContextProvider implements IPageContextProvider {
  private readonly pageService = inject(ChatAdvisorPageService);
  private readonly globalMessageService = inject(DcpGlobalMessageService);
  private readonly currentProductService = inject(CurrentProductService);
  private readonly activeCartService = inject(DcpActiveCartService);

  readonly page$: Observable<string | null> = this.pageService.currentPage$;

  readonly errorCode$: Observable<string | null> = this.globalMessageService.getActiveMessages().pipe(
    // Tail-message error-code: last-added message wins. Returns null when
    // no messages are active — matches the NavigationEnd reset semantics
    // already implemented in DcpGlobalMessageService.resetMessages().
    map((messages) => messages[messages.length - 1]?.errorCode ?? null),
    distinctUntilChanged(),
  );

  // Deferred until product decides on the derivation rule. Field stays wired
  // so the backend contract is honoured; null means "unknown" and the
  // knowledge tool substitutes 'direct' downstream.
  readonly businessModel$: Observable<'direct' | 'indirect' | null> = of(null);

  readonly productId$: Observable<string | null> = this.currentProductService.getProduct().pipe(
    map((product) => product?.code ?? null),
    distinctUntilChanged(),
  );

  readonly cartId$: Observable<string | null> = this.activeCartService.getActiveCartId().pipe(
    // Spartacus emits '' when no cart exists (e.g. registration flow) —
    // normalise to null so downstream contextJson can omit the key.
    map((id) => id || null),
    distinctUntilChanged(),
  );
}
